import { Link } from 'react-router-dom';
import { HeartPulse, ShieldCheck, FlaskConical, Activity, FileSearch, ArrowRight, ArrowLeft } from 'lucide-react';
import { useTenantSlug } from '@/hooks/useConsoleUrl';
import { useCapabilities } from '@/hooks/useCapabilities';
import { launchUrl } from '@/utils/subdomain';

const sections = [
  {
    to: '/verify/fitness',
    capability: 'fitness' as const,
    icon: ShieldCheck,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    title: 'Architecture Fitness',
    description: 'Fitness test compliance across repositories. See which checks pass or fail on each repo.',
  },
  {
    to: '/verify/tests',
    icon: FlaskConical,
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    title: 'Test Reports',
    description: 'Integration test results across services with pass/fail status, duration, and regression detection.',
  },
  {
    to: '/verify/ci',
    icon: Activity,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    title: 'CI/CD Status',
    description: 'Real-time GitHub Actions workflow status across all connected repositories.',
  },
  {
    to: '/verify/apis',
    icon: FileSearch,
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400',
    title: 'API Contracts',
    description: 'Browse OpenAPI specs and detect drift between deployed APIs and repository definitions.',
  },
];

export default function VerifyHubPage() {
  // The dashboard lives on the launch home (the tenant's bare host) — a
  // cross-host hop, and one that needs the slug to have a host at all.
  const slug = useTenantSlug();
  const { isDisabled } = useCapabilities();
  return (
    <div className="p-8">
      {slug && (
        <div className="mb-2">
          <a
            href={launchUrl(slug)}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </a>
        </div>
      )}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center">
            <HeartPulse className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Checks</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Automated quality gates — catch regressions before they reach production
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {sections.map((s) => {
          // A deliberately disabled capability keeps its tile but stops
          // inviting: de-emphasized, no link, a note instead of a nudge
          // (guided-repo-setup Phase D).
          if ('capability' in s && s.capability && isDisabled(s.capability)) {
            return (
              <div
                key={s.to}
                className="flex items-center gap-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 opacity-60"
              >
                <div className={`w-12 h-12 rounded-xl ${s.iconBg} flex items-center justify-center shrink-0`}>
                  <s.icon className={`w-6 h-6 ${s.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">{s.title}</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Disabled in{' '}
                    <Link to="/settings/repo-roles" className="text-blue-600 hover:underline dark:text-blue-400">
                      Settings ▸ Repository roles
                    </Link>
                    .
                  </p>
                </div>
              </div>
            );
          }
          return (
            <Link
              key={s.to}
              to={s.to}
              className="group flex items-center gap-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-md transition-all"
            >
              <div className={`w-12 h-12 rounded-xl ${s.iconBg} flex items-center justify-center shrink-0`}>
                <s.icon className={`w-6 h-6 ${s.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">{s.title}</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">{s.description}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-400 group-hover:translate-x-1 transition-all shrink-0" />
            </Link>
          );
        })}
      </div>

      <div className="mt-8 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Verify pages are being migrated from the portal. Fitness tests and test reports will be available first.
        </p>
      </div>
    </div>
  );
}
